// « Les heures d'or »: CRTC calling hours, in the LEAD's local time. Every yes/no answer is computed by the dialer's
// own withinCallingHours() with the default (CRTC) windows, so the quiz follows the code.
import {
  DEFAULT_CALLING_HOURS,
  nextCallingWindow,
  withinCallingHours,
} from "../../domain/time/callingHours";
import { zonedTimeToUtc } from "../../domain/torontoTime";
import type { I18nArgs, QuizQuestion } from "./types";

const K = "gavion.training.content.golden_hours";

export type HoursScenario = {
  id: string;
  /** Calendar date and wall time where the rep is (America/Toronto: Montréal and Toronto share it). */
  date: string;
  time: string;
  repTz: string;
  /** The lead's time zone (companies.timezone). */
  leadTz: string;
  /** The lead's local wall time at that instant (for the explanation). */
  leadTime: string;
};

export const HOURS_SCENARIOS: readonly HoursScenario[] = [
  // Tuesday 21:45: the weekday window ends at 21:30
  {
    id: "tue_late",
    date: "2026-10-20",
    time: "21:45",
    repTz: "America/Toronto",
    leadTz: "America/Toronto",
    leadTime: "21:45",
  },
  // Saturday 10:15: the weekend window opens at 10:00
  {
    id: "sat_morning",
    date: "2026-10-24",
    time: "10:15",
    repTz: "America/Toronto",
    leadTz: "America/Toronto",
    leadTime: "10:15",
  },
  // Monday 11:30 in Montréal is 8:30 in Vancouver: the lead's clock decides
  {
    id: "vancouver",
    date: "2026-10-19",
    time: "11:30",
    repTz: "America/Toronto",
    leadTz: "America/Vancouver",
    leadTime: "08:30",
  },
  // Sunday 18:00 sharp: the end of the window is excluded
  {
    id: "sun_close",
    date: "2026-10-25",
    time: "18:00",
    repTz: "America/Toronto",
    leadTz: "America/Toronto",
    leadTime: "18:00",
  },
];

export const hoursInstant = (s: HoursScenario): Date =>
  zonedTimeToUtc(s.date, s.time, s.repTz);

/** The dialer's verdict for a scenario (DEFAULT_CALLING_HOURS = CRTC). */
export const canCall = (s: HoursScenario): boolean =>
  withinCallingHours(hoursInstant(s), s.leadTz, DEFAULT_CALLING_HOURS);

/** The windows themselves, for every text that states the rule. */
export const hoursArgs = (): I18nArgs => ({
  weekday_start: { time: DEFAULT_CALLING_HOURS.weekday[0] },
  weekday_end: { time: DEFAULT_CALLING_HOURS.weekday[1] },
  weekend_start: { time: DEFAULT_CALLING_HOURS.weekend[0] },
  weekend_end: { time: DEFAULT_CALLING_HOURS.weekend[1] },
});

const yesNo = (s: HoursScenario): QuizQuestion => ({
  id: s.id,
  promptKey: `${K}.${s.id}.prompt`,
  promptArgs: { time: { time: s.time }, day: { weekday: s.date } },
  options: [
    { id: "yes", labelKey: `${K}.yes` },
    { id: "no", labelKey: `${K}.no` },
  ],
  answer: canCall(s) ? "yes" : "no",
  explanationKey: `${K}.${s.id}.explanation`,
  explanationArgs: { ...hoursArgs(), lead_time: { time: s.leadTime } },
});

/**
 * Dec 25 and Jan 1 (owner decision 2026-10-14): no call all day, by the lead's local date, every year, whatever the
 * holiday list in the call settings says. The answer is the dialer's own verdict on Christmas morning (a Friday in
 * 2026, inside the weekday window) and on the next window it proposes.
 */
const XMAS = zonedTimeToUtc("2026-12-25", "10:30", "America/Toronto");
export const holidayVerdict = (): "blocked" | "weekend_hours" | "open" => {
  if (withinCallingHours(XMAS, "America/Toronto", DEFAULT_CALLING_HOURS))
    return "open";
  const next = nextCallingWindow(
    XMAS,
    "America/Toronto",
    DEFAULT_CALLING_HOURS,
  );
  // blocked all day: the next window is Dec 26 at opening (a Saturday: 10:00)
  return next.getTime() ===
    zonedTimeToUtc("2026-12-26", "10:00", "America/Toronto").getTime()
    ? "blocked"
    : "weekend_hours";
};

export const HOLIDAY_QUESTION: QuizQuestion = {
  id: "holidays",
  promptKey: `${K}.holidays.prompt`,
  options: [
    { id: "blocked", labelKey: `${K}.holidays.blocked` },
    { id: "weekend_hours", labelKey: `${K}.holidays.weekend_hours` },
    { id: "open", labelKey: `${K}.holidays.config_list` },
  ],
  answer: holidayVerdict(),
  explanationKey: `${K}.holidays.explanation`,
};

export const GOLDEN_HOURS_QUESTIONS: readonly QuizQuestion[] = [
  ...HOURS_SCENARIOS.map(yesNo),
  HOLIDAY_QUESTION,
];
